import { Button, Card, CardTitle, Input, NewPasswordComponent } from "@dune/ui";
import "@dune/ui/globals.css";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

const passwordSchema = z
	.string()
	.min(8, "Le mot de passe doit contenir au moins 8 caractères")
	.regex(/[a-z]/, "Le mot de passe doit contenir au moins une lettre minuscule")
	.regex(/[A-Z]/, "Le mot de passe doit contenir au moins une lettre majuscule")
	.regex(/\d/, "Le mot de passe doit contenir au moins un chiffre")
	.regex(/[!@#$%^&*(),.?":{}|<>]/, "Au moins 1 caractère spécial");

interface ResetPasswordFormProps {
	withBackToLogin?: boolean;
	onSubmit: (props: { token: string; password: string }) => void;
}

export default function ResetPasswordForm({
	withBackToLogin,
	onSubmit,
}: Readonly<ResetPasswordFormProps>) {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordVisible, setPasswordVisible] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [passwordsMatchError, setPasswordsMatchError] = useState<string | null>(
		null,
	);
	const [token, setToken] = useState<string | null>(null);
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const tokenFromUrl = params.get("token");
		if (tokenFromUrl) {
			setToken(tokenFromUrl);
		}
	}, []);

	const validatePassword = useCallback((password: string) => {
		const result = passwordSchema.safeParse(password);
		if (result.success) {
			setErrors([]);
		} else {
			const errorMessages = result.error.issues.map((err) => err.message);
			setErrors(errorMessages);
		}
	}, []);

	const togglePasswordVisibility = () => {
		setPasswordVisible(!passwordVisible);
	};

	useEffect(() => {
		setIsButtonDisabled(
			errors.length > 0 ||
				password.length === 0 ||
				confirmPassword.length === 0 ||
				!token,
		);
	}, [password, confirmPassword, errors, token]);

	const handleOnSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		validatePassword(password);

		if (password !== confirmPassword) {
			setPasswordsMatchError("Les mots de passe ne correspondent pas");
		} else {
			setPasswordsMatchError(null);
		}

		if (errors.length === 0 && password === confirmPassword && token) {
			onSubmit({
				token: token,
				password: password,
			});
		}
	};

	return (
		<div className="bg-background-blue h-screen flex items-center justify-center">
			<div className="absolute top-0 left-0 p-6">
				<img src="/dune_white_logo.png" alt="Dune Logo" className="w-32" />
			</div>
			<div>
				<Card className="p-10 flex flex-col gap-8 max-w-[420px] overflow-auto text-center">
					<CardTitle className="text-4xl">
						Définissez votre mot de passe
					</CardTitle>
					<form className="flex flex-col gap-4" onSubmit={handleOnSubmit}>
						<div className="flex flex-col items-start gap-3">
							<div className="relative w-full">
								<NewPasswordComponent
									password={password}
									setPassword={setPassword}
									isVisible={passwordVisible}
									toggleVisibility={togglePasswordVisibility}
								/>
							</div>
						</div>

						<div className="flex flex-col items-start gap-3">
							<div className="relative w-full">
								<Input
									type={passwordVisible ? "text" : "password"}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Confirmer le mot de passe"
								/>
							</div>
							{passwordsMatchError && (
								<div className="text-red-500 text-sm mt-2">
									{passwordsMatchError}
								</div>
							)}
						</div>
						<div className="mt-2 text-red-500 text-sm">
							{errors.length > 0 && (
								<ul>
									{errors.map((error) => (
										<li key={error}>{error}</li>
									))}
								</ul>
							)}
						</div>
						<Button
							variant="outline"
							type="submit"
							className="h-11 w-full"
							disabled={isButtonDisabled}
						>
							Suivant
						</Button>
						{withBackToLogin && (
							<div className="mt-4">
								<a
									href="/login"
									className="text-text-blue hover:text-blue-700 font-semibold flex justify-center w-full"
								>
									Retour à la connexion
								</a>
							</div>
						)}
					</form>
				</Card>
			</div>
		</div>
	);
}
